(() => {
  'use strict';

  const ROOT_SELECTOR =
    '[data-manta-configurator]';

  const EXPECTED_OPTION_NAMES = [
    'System Type',
    'Material',
    'Noise - Pipe Size'
  ];

  const DIMENSIONS = [
    'system',
    'material',
    'noise',
    'pipe'
  ];

  const COMBINED_SEPARATOR =
    ' | ';


  function parseJSON(element) {
    if (!element) {
      return null;
    }

    try {
      return JSON.parse(
        element.textContent
      );
    } catch (error) {
      console.error(
        '[TCS Manta Configurator] Invalid JSON.',
        error
      );

      return null;
    }
  }


  function unique(values) {
    return [
      ...new Set(values)
    ];
  }


  /*
   * ------------------------------------------------------------
   * BUILD FOUR-DIMENSION RECORDS
   * ------------------------------------------------------------
   */

  function buildRecords(variants) {
    const records = [];

    for (const variant of variants) {
      if (
        !Array.isArray(variant.options) ||
        variant.options.length !== 3
      ) {
        return null;
      }

      const system =
        variant.options[0];

      const material =
        variant.options[1];

      const combined =
        variant.options[2];

      if (
        typeof combined !== 'string' ||
        !combined.includes(
          COMBINED_SEPARATOR
        )
      ) {
        return null;
      }

      const splitPosition =
        combined.indexOf(
          COMBINED_SEPARATOR
        );

      const noise =
        combined
          .slice(
            0,
            splitPosition
          )
          .trim();

      const pipe =
        combined
          .slice(
            splitPosition +
            COMBINED_SEPARATOR.length
          )
          .trim();

      if (
        !system ||
        !material ||
        !noise ||
        !pipe
      ) {
        return null;
      }

      records.push({
        id:
          variant.id,

        sku:
          variant.sku || '',

        available:
          Boolean(
            variant.available
          ),

        system,
        material,
        noise,
        pipe,

        variant
      });
    }

    return records;
  }


  function combinationsAreUnique(
    records
  ) {
    const seen =
      new Set();

    for (const record of records) {
      const key = [
        record.system,
        record.material,
        record.noise,
        record.pipe
      ].join(' || ');

      if (
        seen.has(key)
      ) {
        return false;
      }

      seen.add(key);
    }

    return true;
  }


  /*
   * ------------------------------------------------------------
   * CUSTOM SELECTORS
   * ------------------------------------------------------------
   */

  function getSelects(root) {
    return {
      system:
        root.querySelector(
          '[data-manta-select="system"]'
        ),

      material:
        root.querySelector(
          '[data-manta-select="material"]'
        ),

      noise:
        root.querySelector(
          '[data-manta-select="noise"]'
        ),

      pipe:
        root.querySelector(
          '[data-manta-select="pipe"]'
        )
    };
  }


  function getState(selects) {
    return {
      system:
        selects.system.value,

      material:
        selects.material.value,

      noise:
        selects.noise.value,

      pipe:
        selects.pipe.value
    };
  }


  function populateSelect(
    select,
    values
  ) {
    while (
      select.options.length > 1
    ) {
      select.remove(1);
    }

    for (const value of values) {
      const option =
        document.createElement(
          'option'
        );

      option.value =
        value;

      option.textContent =
        value;

      select.appendChild(
        option
      );
    }
  }


  function populateAllSelects(
    records,
    selects
  ) {
    populateSelect(
      selects.system,
      unique(
        records.map(
          record =>
            record.system
        )
      )
    );

    populateSelect(
      selects.material,
      unique(
        records.map(
          record =>
            record.material
        )
      )
    );

    const noiseValues =
      unique(
        records.map(
          record =>
            record.noise
        )
      );

    const noiseOrder = [
      'Quiet',
      'Medium',
      'Loud'
    ];

    noiseValues.sort(
      (a, b) => {
        const ai =
          noiseOrder.indexOf(a);

        const bi =
          noiseOrder.indexOf(b);

        if (
          ai !== -1 &&
          bi !== -1
        ) {
          return ai - bi;
        }

        if (ai !== -1) {
          return -1;
        }

        if (bi !== -1) {
          return 1;
        }

        return (
          a.localeCompare(b)
        );
      }
    );

    populateSelect(
      selects.noise,
      noiseValues
    );

    populateSelect(
      selects.pipe,
      unique(
        records.map(
          record =>
            record.pipe
        )
      )
    );
  }


  /*
   * ------------------------------------------------------------
   * AVAILABILITY
   * ------------------------------------------------------------
   */

  function recordMatchesState(
    record,
    state,
    ignoredDimension = null
  ) {
    for (
      const dimension
      of DIMENSIONS
    ) {
      if (
        dimension ===
        ignoredDimension
      ) {
        continue;
      }

      const selected =
        state[dimension];

      if (
        selected &&
        record[dimension] !== selected
      ) {
        return false;
      }
    }

    return true;
  }


  function refreshAvailability(
    records,
    selects
  ) {
    const state =
      getState(selects);

    for (
      const dimension
      of DIMENSIONS
    ) {
      const select =
        selects[dimension];

      for (
        let i = 1;
        i < select.options.length;
        i += 1
      ) {
        const option =
          select.options[i];

        let possible = false;


        /*
         * SYSTEM TYPE IS SPECIAL
         *
         * Every System Type remains selectable as long
         * as at least one available real SKU exists
         * somewhere under that System Type.
         *
         * Existing Material / Noise / Pipe selections
         * are deliberately ignored here.
         */
        if (
          dimension === 'system'
        ) {
          possible =
            records.some(
              record =>
                record.available &&
                record.system ===
                  option.value
            );
        } else {

          /*
           * Material / Noise / Pipe continue to use
           * normal cross-filtering against all other
           * selections.
           */
          possible =
            records.some(
              record => {
                if (
                  !record.available
                ) {
                  return false;
                }

                if (
                  record[dimension] !==
                  option.value
                ) {
                  return false;
                }

                return (
                  recordMatchesState(
                    record,
                    state,
                    dimension
                  )
                );
              }
            );
        }

        option.disabled =
          !possible;
      }
    }
  }


  function findResolvedVariant(
    records,
    state
  ) {
    if (
      DIMENSIONS.some(
        dimension =>
          !state[dimension]
      )
    ) {
      return null;
    }

    const matches =
      records.filter(
        record => {
          if (
            !record.available
          ) {
            return false;
          }

          return (
            DIMENSIONS.every(
              dimension =>
                record[dimension] ===
                state[dimension]
            )
          );
        }
      );

    if (
      matches.length !== 1
    ) {
      return null;
    }

    return matches[0];
  }


  /*
   * ------------------------------------------------------------
   * SHOPIFY PRODUCT FORM
   * ------------------------------------------------------------
   */

  function getProductForm(root) {
    const id =
      root.dataset
        .productFormId;

    if (!id) {
      return null;
    }

    return (
      document.getElementById(id)
    );
  }


  function getSection(root) {
    return (
      document.getElementById(
        `MainProduct-${
          root.dataset.sectionId
        }`
      )
    );
  }


  function getAddButton(root) {
    const form =
      getProductForm(root);

    if (!form) {
      return null;
    }

    return (
      form.querySelector(
        '[name="add"]'
      )
    );
  }


  function setVariantId(
    root,
    variantId
  ) {
    const sectionId =
      root.dataset.sectionId;

    const form =
      getProductForm(root);

    if (form) {
      const input =
        form.querySelector(
          'input[name="id"]'
        );

      if (input) {
        input.disabled =
          false;

        input.value =
          variantId || '';

        input.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true
            }
          )
        );
      }
    }

    const installment =
      document.getElementById(
        `product-form-installment-${sectionId}`
      );

    if (installment) {
      const input =
        installment.querySelector(
          'input[name="id"]'
        );

      if (input) {
        input.value =
          variantId || '';
      }
    }
  }


  function disablePurchase(root) {
    const button =
      getAddButton(root);

    if (button) {
      button.disabled =
        true;

      button.setAttribute(
        'aria-disabled',
        'true'
      );
    }

    const section =
      getSection(root);

    const dynamicCheckout =
      section?.querySelector(
        '.shopify-payment-button'
      );

    if (dynamicCheckout) {
      dynamicCheckout.hidden =
        true;
    }

    setVariantId(
      root,
      ''
    );
  }


  /*
   * ------------------------------------------------------------
   * PRICE
   * ------------------------------------------------------------
   */

  function showPriceRange(root) {
    const sectionId =
      root.dataset.sectionId;

    const range =
      document.getElementById(
        `MantaPriceRange-${sectionId}`
      );

    const price =
      document.getElementById(
        `price-${sectionId}`
      );

    const installment =
      getSection(root)?.querySelector(
        '[data-manta-installment]'
      );

    if (range) {
      range.hidden =
        false;
    }

    if (price) {
      price.hidden =
        true;
    }

    if (installment) {
      installment.hidden =
        true;
    }
  }


  function showVariantPrice(root) {
    const sectionId =
      root.dataset.sectionId;

    const range =
      document.getElementById(
        `MantaPriceRange-${sectionId}`
      );

    const price =
      document.getElementById(
        `price-${sectionId}`
      );

    const installment =
      getSection(root)?.querySelector(
        '[data-manta-installment]'
      );

    if (range) {
      range.hidden =
        true;
    }

    if (price) {
      price.hidden =
        false;

      price.classList.remove(
        'visibility-hidden'
      );
    }

    if (installment) {
      installment.hidden =
        false;
    }
  }


  /*
   * ------------------------------------------------------------
   * SKU / INVENTORY / PICKUP
   * ------------------------------------------------------------
   */

  function setAncillaryVisible(
    root,
    visible
  ) {
    const sectionId =
      root.dataset.sectionId;

    const sku =
      document.getElementById(
        `Sku-${sectionId}`
      );

    const inventory =
      document.getElementById(
        `Inventory-${sectionId}`
      );

    const pickup =
      getSection(root)?.querySelector(
        'pickup-availability'
      );

    if (sku) {
      sku.hidden =
        !visible;
    }

    if (inventory) {
      inventory.hidden =
        !visible;
    }

    if (pickup) {
      pickup.hidden =
        !visible;
    }
  }


  /*
   * ------------------------------------------------------------
   * PRODUCT MEDIA
   * ------------------------------------------------------------
   */

  function updateMedia(
    root,
    record
  ) {
    const media =
      record.variant
        .featured_media;

    if (!media) {
      return;
    }

    const sectionId =
      root.dataset.sectionId;

    const galleries =
      document.querySelectorAll(
        `[id^="MediaGallery-${sectionId}"]`
      );

    galleries.forEach(
      gallery => {
        if (
          typeof gallery
            .setActiveMedia ===
          'function'
        ) {
          gallery.setActiveMedia(
            `${sectionId}-${media.id}`,
            true
          );
        }
      }
    );
  }


  /*
   * ------------------------------------------------------------
   * URL / SHARE
   * ------------------------------------------------------------
   */

  function updateURL(
    root,
    record
  ) {
    const url =
      root.dataset.productUrl;

    if (!url) {
      return;
    }

    window.history
      .replaceState(
        {},
        '',
        `${url}?variant=${record.id}`
      );

    const share =
      document.getElementById(
        `Share-${root.dataset.sectionId}`
      );

    if (
      share &&
      typeof share.updateUrl ===
      'function'
    ) {
      share.updateUrl(
        `${window.shopUrl}${url}` +
        `?variant=${record.id}`
      );
    }
  }


  /*
   * ------------------------------------------------------------
   * SERVER-RENDERED VARIANT INFO
   * ------------------------------------------------------------
   */

  async function refreshVariantInfo(
    root,
    record
  ) {
    const sectionId =
      root.dataset.sectionId;

    const productUrl =
      root.dataset.productUrl;

    if (
      !sectionId ||
      !productUrl
    ) {
      return false;
    }

    root.dataset.requestedVariantId =
      String(record.id);

    try {
      const response =
        await fetch(
          `${productUrl}` +
          `?variant=${record.id}` +
          `&section_id=${sectionId}`
        );

      if (!response.ok) {
        return false;
      }

      const responseText =
        await response.text();

      if (
        root.dataset
          .requestedVariantId !==
        String(record.id)
      ) {
        return false;
      }

      const html =
        new DOMParser()
          .parseFromString(
            responseText,
            'text/html'
          );


      /*
       * PRICE
       */

      const priceDestination =
        document.getElementById(
          `price-${sectionId}`
        );

      const priceSource =
        html.getElementById(
          `price-${sectionId}`
        );

      if (
        priceDestination &&
        priceSource
      ) {
        priceDestination.innerHTML =
          priceSource.innerHTML;
      }


      /*
       * SKU
       */

      const skuDestination =
        document.getElementById(
          `Sku-${sectionId}`
        );

      const skuSource =
        html.getElementById(
          `Sku-${sectionId}`
        );

      if (
        skuDestination &&
        skuSource
      ) {
        skuDestination.innerHTML =
          skuSource.innerHTML;

        skuDestination
          .classList.toggle(
            'visibility-hidden',
            skuSource
              .classList
              .contains(
                'visibility-hidden'
              )
          );
      }


      /*
       * INVENTORY
       */

      const inventoryDestination =
        document.getElementById(
          `Inventory-${sectionId}`
        );

      const inventorySource =
        html.getElementById(
          `Inventory-${sectionId}`
        );

      if (
        inventoryDestination &&
        inventorySource
      ) {
        inventoryDestination.innerHTML =
          inventorySource.innerHTML;

        inventoryDestination
          .classList.toggle(
            'visibility-hidden',
            inventorySource
              .innerText
              .trim() === ''
          );
      }


      /*
       * ADD TO CART BUTTON
       */

      const sourceButton =
        html.getElementById(
          `ProductSubmitButton-${sectionId}`
        );

      const destinationButton =
        getAddButton(root);

      if (
        !sourceButton ||
        !destinationButton
      ) {
        return false;
      }

      const disabled =
        sourceButton
          .hasAttribute(
            'disabled'
          );

      destinationButton.disabled =
        disabled;

      if (disabled) {
        destinationButton.setAttribute(
          'aria-disabled',
          'true'
        );
      } else {
        destinationButton
          .removeAttribute(
            'aria-disabled'
          );
      }

      const sourceText =
        sourceButton.querySelector(
          'span'
        );

      const destinationText =
        destinationButton.querySelector(
          'span'
        );

      if (
        sourceText &&
        destinationText
      ) {
        destinationText.textContent =
          sourceText.textContent
            .trim();
      }

      return true;

    } catch (error) {
      console.error(
        '[TCS Manta Configurator] ' +
        'Unable to refresh selected variant information.',
        error
      );

      return false;
    }
  }


  /*
   * ------------------------------------------------------------
   * PICKUP AVAILABILITY
   * ------------------------------------------------------------
   */

  function updatePickup(
    root,
    record
  ) {
    const pickup =
      getSection(root)?.querySelector(
        'pickup-availability'
      );

    if (
      pickup &&
      typeof pickup
        .fetchAvailability ===
      'function'
    ) {
      pickup.fetchAvailability(
        record.id
      );
    }
  }


  /*
   * ------------------------------------------------------------
   * STATUS / DEBUG
   * ------------------------------------------------------------
   */

  function setStatus(
    root,
    text
  ) {
    const element =
      root.querySelector(
        '[data-manta-status]'
      );

    if (element) {
      element.textContent =
        text;
    }
  }


  function setDebug(
    root,
    record
  ) {
    const element =
      root.querySelector(
        '[data-manta-debug]'
      );

    if (!element) {
      return;
    }

    if (!record) {
      element.textContent =
        'None';

      return;
    }

    element.textContent =
      `${record.sku || 'No SKU'} ` +
      `(Variant ID ${record.id})`;
  }


  /*
   * ------------------------------------------------------------
   * UNRESOLVED STATE
   * ------------------------------------------------------------
   */

  function setUnresolved(
    root
  ) {
    root.removeAttribute(
      'data-resolved-variant-id'
    );

    disablePurchase(root);

    showPriceRange(root);

    setAncillaryVisible(
      root,
      false
    );

    setDebug(
      root,
      null
    );
  }


  /*
   * ------------------------------------------------------------
   * SYSTEM TYPE CHANGE
   * ------------------------------------------------------------
   *
   * System Type is the primary Manta decision.
   *
   * When it changes:
   * - preserve the newly selected System Type
   * - clear Material
   * - clear Noise Level
   * - clear Pipe Size
   * - clear the resolved Shopify variant
   * - return to price range
   * - disable Add to Cart
   * - recalculate all compatible downstream choices
   */

  function handleSystemChange(
    root,
    records,
    selects
  ) {
    selects.material.value =
      '';

    selects.noise.value =
      '';

    selects.pipe.value =
      '';

    setUnresolved(root);

    refreshAvailability(
      records,
      selects
    );

    if (
      selects.system.value
    ) {
      setStatus(
        root,
        'Choose Material, Noise Level and Pipe Size to continue.'
      );
    } else {
      setStatus(
        root,
        'Select your exhaust configuration.'
      );
    }
  }


  /*
   * ------------------------------------------------------------
   * RESOLVED VARIANT
   * ------------------------------------------------------------
   */

  async function applyVariant(
    root,
    record
  ) {
    disablePurchase(root);

    setVariantId(
      root,
      record.id
    );

    root.dataset.resolvedVariantId =
      String(record.id);

    setStatus(
      root,
      'Loading selected configuration...'
    );

    setDebug(
      root,
      record
    );

    updateMedia(
      root,
      record
    );

    updateURL(
      root,
      record
    );

    updatePickup(
      root,
      record
    );

    const refreshed =
      await refreshVariantInfo(
        root,
        record
      );

    if (
      root.dataset
        .resolvedVariantId !==
      String(record.id)
    ) {
      return;
    }

    if (!refreshed) {
      setStatus(
        root,
        'Unable to load this configuration. Please select again.'
      );

      disablePurchase(root);

      return;
    }

    showVariantPrice(root);

    setAncillaryVisible(
      root,
      true
    );

    const dynamicCheckout =
      getSection(root)?.querySelector(
        '.shopify-payment-button'
      );

    if (dynamicCheckout) {
      dynamicCheckout.hidden =
        false;
    }

    setStatus(
      root,
      'Configuration selected.'
    );
  }


  /*
   * ------------------------------------------------------------
   * CUSTOMER CHANGE
   * ------------------------------------------------------------
   */

  function handleChange(
    root,
    records,
    selects
  ) {
    refreshAvailability(
      records,
      selects
    );

    const state =
      getState(selects);

    const record =
      findResolvedVariant(
        records,
        state
      );

    if (!record) {
      setUnresolved(root);

      const selectedCount =
        DIMENSIONS.filter(
          dimension =>
            Boolean(
              state[dimension]
            )
        ).length;

      if (
        selectedCount === 0
      ) {
        setStatus(
          root,
          'Select your exhaust configuration.'
        );
      } else {
        setStatus(
          root,
          'Complete all four options to continue.'
        );
      }

      return;
    }

    applyVariant(
      root,
      record
    );
  }


  /*
   * ------------------------------------------------------------
   * EXPLICIT ?variant= URL
   * ------------------------------------------------------------
   */

  function loadExplicitVariant(
    root,
    records,
    selects
  ) {
    const id =
      root.dataset
        .selectedVariantId;

    if (!id) {
      return false;
    }

    const record =
      records.find(
        candidate =>
          String(candidate.id) ===
          String(id)
      );

    if (!record) {
      return false;
    }

    selects.system.value =
      record.system;

    selects.material.value =
      record.material;

    selects.noise.value =
      record.noise;

    selects.pipe.value =
      record.pipe;

    refreshAvailability(
      records,
      selects
    );

    applyVariant(
      root,
      record
    );

    return true;
  }


  /*
   * ------------------------------------------------------------
   * INITIALISATION
   * ------------------------------------------------------------
   */

  function initialise(root) {
    if (
      root.dataset
        .mantaInitialised ===
      'true'
    ) {
      return;
    }

    const optionNames =
      parseJSON(
        root.querySelector(
          '[data-manta-option-names]'
        )
      );

    const variants =
      parseJSON(
        root.querySelector(
          '[data-manta-variants]'
        )
      );


    /*
     * Validate required Shopify backend structure.
     */

    if (
      !Array.isArray(
        optionNames
      ) ||
      optionNames.length !== 3 ||
      !EXPECTED_OPTION_NAMES
        .every(
          (name, index) =>
            optionNames[index] ===
            name
        )
    ) {
      console.error(
        '[TCS Manta Configurator] ' +
        'Incorrect Shopify option structure.',
        optionNames
      );

      return;
    }


    if (
      !Array.isArray(variants) ||
      variants.length === 0
    ) {
      console.error(
        '[TCS Manta Configurator] ' +
        'No product variants found.'
      );

      return;
    }


    const records =
      buildRecords(
        variants
      );

    if (!records) {
      console.error(
        '[TCS Manta Configurator] ' +
        'Noise - Pipe Size values must use " | " as the separator.'
      );

      return;
    }


    if (
      !combinationsAreUnique(
        records
      )
    ) {
      console.error(
        '[TCS Manta Configurator] ' +
        'Duplicate four-option combinations detected.'
      );

      return;
    }


    const selects =
      getSelects(root);

    if (
      DIMENSIONS.some(
        dimension =>
          !selects[dimension]
      )
    ) {
      console.error(
        '[TCS Manta Configurator] ' +
        'Configurator dropdowns are missing.'
      );

      return;
    }


    populateAllSelects(
      records,
      selects
    );


    root.dataset
      .mantaInitialised =
      'true';


    root.querySelector(
      '[data-manta-custom]'
    ).hidden =
      false;


    /*
     * SYSTEM TYPE:
     *
     * Always freely selectable.
     * Changing it resets the other three options.
     */
    selects.system
      .addEventListener(
        'change',
        () => {
          handleSystemChange(
            root,
            records,
            selects
          );
        }
      );


    /*
     * MATERIAL / NOISE / PIPE:
     *
     * Continue using ordinary bidirectional availability logic.
     */
    [
      'material',
      'noise',
      'pipe'
    ].forEach(
      dimension => {
        selects[dimension]
          .addEventListener(
            'change',
            () => {
              handleChange(
                root,
                records,
                selects
              );
            }
          );
      }
    );


    const explicit =
      loadExplicitVariant(
        root,
        records,
        selects
      );


    if (!explicit) {
      refreshAvailability(
        records,
        selects
      );

      setUnresolved(root);

      setStatus(
        root,
        'Select your exhaust configuration.'
      );
    }
  }


  function initialiseAll() {
    document
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(
        initialise
      );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAll
    );
  } else {
    initialiseAll();
  }


  document.addEventListener(
    'shopify:section:load',
    initialiseAll
  );

})();